import { motion } from "framer-motion";
import { Phone, Mail, MapPin, Instagram, Clock } from "lucide-react";
import { BRAND, HOURS } from "../data/menu";

export default function Reserve() {
  return (
    <div data-testid="reserve-page" className="pt-32 pb-24">
      <header className="max-w-7xl mx-auto px-6 md:px-10 mb-20">
        <p className="text-[10px] uppercase tracking-[0.3em] text-ember mb-6">Pickup · Catering · Large orders</p>
        <h1 className="font-serif text-6xl md:text-8xl leading-[0.9] tracking-tighter text-charred">
          Just give us<br /><span className="italic text-ember">a call.</span>
        </h1>
        <p className="mt-8 max-w-xl text-muted2 text-lg leading-relaxed">
          For pickup orders, catering, or any big-day plans, the quickest way
          to lock it in is over the phone. We&rsquo;ll talk you through what
          we&rsquo;re firing tonight and get your pies on the schedule.
        </p>
      </header>

      <div className="max-w-7xl mx-auto px-6 md:px-10 grid md:grid-cols-12 gap-12 items-start">
        {/* Primary call CTA */}
        <motion.div
          initial={{ opacity: 0, y: 18 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="md:col-span-7"
          data-testid="reserve-call-block"
        >
          <div className="bg-charred text-cream p-10 md:p-16">
            <p className="text-[10px] uppercase tracking-[0.3em] text-cream/60 mb-6">Call us</p>
            <a
              href={BRAND.phoneHref}
              data-testid="call-cta"
              className="font-serif text-5xl md:text-7xl leading-[0.95] tracking-tight hover:text-ember transition-colors inline-flex items-center gap-5 group"
            >
              <Phone size={48} className="opacity-80 group-hover:text-ember transition-colors" />
              {BRAND.phone}
            </a>
            <p className="mt-8 text-cream/80 max-w-md leading-relaxed">
              One call. Two minutes. We&rsquo;ll confirm your pies, pickup
              time, or catering plan on the spot.
            </p>

            <div className="mt-12 pt-8 border-t border-cream/15 space-y-4">
              <a
                href={BRAND.emailHref}
                data-testid="reserve-email"
                className="flex items-center gap-3 hover:text-ember transition-colors"
              >
                <Mail size={18} />
                <span className="font-serif text-2xl">{BRAND.email}</span>
              </a>
              <a
                href={BRAND.instagramUrl}
                target="_blank"
                rel="noreferrer"
                data-testid="reserve-instagram"
                className="flex items-center gap-3 hover:text-ember transition-colors"
              >
                <Instagram size={18} />
                <span className="font-serif text-2xl">{BRAND.instagram}</span>
              </a>
            </div>
          </div>
        </motion.div>

        {/* Side info: hours + address */}
        <motion.aside
          initial={{ opacity: 0, y: 18 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, delay: 0.1 }}
          className="md:col-span-5 md:pt-2"
          data-testid="reserve-aside"
        >
          <p className="text-[10px] uppercase tracking-[0.3em] text-ember mb-4">When we&rsquo;re firing</p>
          <div className="border-t border-charred/20 mb-10">
            {HOURS.map((h) => (
              <div key={h.day} className="flex justify-between py-4 border-b border-edge gap-4">
                <span className="font-serif text-xl text-charred leading-tight">{h.day}</span>
                <span className="text-sm tracking-wider text-muted2 self-center text-right">{h.hours}</span>
              </div>
            ))}
          </div>

          <p className="text-[10px] uppercase tracking-[0.3em] text-ember mb-4">Find us</p>
          <a
            href={BRAND.mapsUrl}
            target="_blank"
            rel="noreferrer"
            data-testid="reserve-address"
            className="flex items-start gap-3 text-charred hover:text-ember transition-colors"
          >
            <MapPin size={18} className="mt-1 shrink-0" />
            <span className="font-serif text-xl leading-snug">{BRAND.address}</span>
          </a>

          <p className="mt-8 text-sm text-muted2 leading-relaxed">
            Walk-ins welcome — but for a hot pie waiting at the counter,
            phone&nbsp;ahead.
          </p>
        </motion.aside>
      </div>
    </div>
  );
}
